import type { ImageFilter } from '@ox/schema';

/**
 * 5×4 color matrices (20 floats), Fabric.js-compatible.
 * Sepia / Vintage / Kodachrome / Technicolor / BlackWhite from fabric.js
 * (MIT — https://github.com/fabricjs/fabric.js). Fancy Product Designer uses the same family.
 * Cold / Old / Milk / Purple / Yellow are tint matrices matching typical FPD filter names.
 */
export const FILTER_MATRICES: Record<Exclude<ImageFilter, 'none' | 'grayscale'>, number[]> = {
  sepia: [0.393, 0.769, 0.189, 0, 0, 0.349, 0.686, 0.168, 0, 0, 0.272, 0.534, 0.131, 0, 0, 0, 0, 0, 1, 0],
  vintage: [
    0.62793, 0.32021, -0.03965, 0, 0.03784, 0.02578, 0.64411, 0.03259, 0, 0.02926, 0.0466, -0.08512,
    0.52416, 0, 0.02023, 0, 0, 0, 1, 0,
  ],
  kodachrome: [
    1.12855, -0.39673, -0.03992, 0, 0.24991, -0.16404, 1.08352, -0.05498, 0, 0.09698, -0.16786,
    -0.56034, 1.60148, 0, 0.13972, 0, 0, 0, 1, 0,
  ],
  technicolor: [
    1.91252, -0.85453, -0.09155, 0, 0.04624, -0.30878, 1.76589, -0.10601, 0, -0.27589, -0.2311,
    -0.75018, 1.84759, 0, 0.12137, 0, 0, 0, 1, 0,
  ],
  blackwhite: [1.5, 1.5, 1.5, 0, -1, 1.5, 1.5, 1.5, 0, -1, 1.5, 1.5, 1.5, 0, -1, 0, 0, 0, 1, 0],
  // Cool temperature (FPD “Cold”)
  cold: [0.9, 0, 0.1, 0, 0, 0, 0.95, 0.15, 0, 0, 0.1, 0.1, 1.15, 0, 0.05, 0, 0, 0, 1, 0],
  // Faded warm antique (FPD “Old” ≈ Brownie-ish)
  old: [
    0.5997, 0.34553, -0.27082, 0, 0.186, -0.0377, 0.86095, 0.15059, 0, -0.1449, 0.24113, -0.07441,
    0.44972, 0, -0.02965, 0, 0, 0, 1, 0,
  ],
  // Soft washed look (FPD “Milk”)
  milk: [1.1, 0.05, 0.05, 0, 0.08, 0.05, 1.05, 0.05, 0, 0.08, 0.05, 0.05, 1.0, 0, 0.1, 0, 0, 0, 1, 0],
  purple: [1.05, 0.05, 0.2, 0, 0.05, 0.05, 0.85, 0.25, 0, 0, 0.15, 0.05, 1.1, 0, 0.08, 0, 0, 0, 1, 0],
  yellow: [1.15, 0.1, 0, 0, 0.08, 0.1, 1.1, 0, 0, 0.06, 0, 0.05, 0.85, 0, 0, 0, 0, 0, 1, 0],
};

export const EDITOR_FILTERS: { id: ImageFilter; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'grayscale', label: 'Grayscale' },
  { id: 'sepia', label: 'Sepia' },
  { id: 'vintage', label: 'Vintage' },
  { id: 'kodachrome', label: 'Kodachrome' },
  { id: 'technicolor', label: 'Technicolor' },
  { id: 'blackwhite', label: 'Black & White' },
  { id: 'cold', label: 'Cold' },
  { id: 'old', label: 'Old' },
  { id: 'milk', label: 'Milk' },
  { id: 'purple', label: 'Purple' },
  { id: 'yellow', label: 'Yellow' },
];

export function resolveImageFilter(
  filter?: ImageFilter | null,
  grayscale?: boolean,
): ImageFilter {
  if (filter && filter !== 'none') return filter;
  if (grayscale) return 'grayscale';
  return 'none';
}

/** Apply Fabric-style matrix (offsets in 0–1) to ImageData in place. */
export function applyColorMatrix(data: ImageData, matrix: number[]): void {
  const px = data.data;
  const m = matrix;
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]! / 255;
    const g = px[i + 1]! / 255;
    const b = px[i + 2]! / 255;
    const a = px[i + 3]! / 255;
    const nr = m[0]! * r + m[1]! * g + m[2]! * b + m[3]! * a + m[4]!;
    const ng = m[5]! * r + m[6]! * g + m[7]! * b + m[8]! * a + m[9]!;
    const nb = m[10]! * r + m[11]! * g + m[12]! * b + m[13]! * a + m[14]!;
    const na = m[15]! * r + m[16]! * g + m[17]! * b + m[18]! * a + m[19]!;
    px[i] = clampByte(nr * 255);
    px[i + 1] = clampByte(ng * 255);
    px[i + 2] = clampByte(nb * 255);
    px[i + 3] = clampByte(na * 255);
  }
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function applyGrayscale(data: ImageData): void {
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    const g = 0.299 * px[i]! + 0.587 * px[i + 1]! + 0.114 * px[i + 2]!;
    px[i] = g;
    px[i + 1] = g;
    px[i + 2] = g;
  }
}

/** Bake filter onto a canvas (browser). Avoids Konva cache which breaks parent clip. */
export function bakeImageFilter(
  source: CanvasImageSource & { width?: number; height?: number; naturalWidth?: number; naturalHeight?: number },
  filter: ImageFilter,
): HTMLCanvasElement | CanvasImageSource {
  if (!filter || filter === 'none') return source;
  const w =
    ('naturalWidth' in source && source.naturalWidth) ||
    ('width' in source && Number(source.width)) ||
    1;
  const h =
    ('naturalHeight' in source && source.naturalHeight) ||
    ('height' in source && Number(source.height)) ||
    1;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return source;
  ctx.drawImage(source as CanvasImageSource, 0, 0);
  const data = ctx.getImageData(0, 0, w, h);
  if (filter === 'grayscale') applyGrayscale(data);
  else applyColorMatrix(data, FILTER_MATRICES[filter]);
  ctx.putImageData(data, 0, 0);
  return c;
}

/** SVG feColorMatrix values string, or null if none. */
export function filterToSvgFeColorMatrix(filter: ImageFilter): string | null {
  if (!filter || filter === 'none') return null;
  if (filter === 'grayscale') {
    // luminance to RGB
    return '0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0 0 0 1 0';
  }
  return FILTER_MATRICES[filter].join(' ');
}
